import { Box, Grid, Typography } from '@mui/material';
import CustomDataGrid from '../../../../common/components/customized/customdatagrid/CustomDataGrid';
import './Inspection.scss';
import { SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  convertToEpoch,
  convertToEpoch2,
  useAbort
} from '../../../../utils/commonFunctions';
import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../app/redux/hooks';
import CustomSelect from '../../../../common/components/customized/customselect/CustomSelect';
import CustomDateTimeCalendar from '../../../../common/components/customized/customdatetimecalendar/CustomDateTimeCalendar';
import { updateToast } from '../../../../common/redux/reducer/commonSlices/toastSlice';
import dayjs from 'dayjs';
import CustomButton from '../../../../common/components/buttons/CustomButton';
import { truckNumbers } from '../../redux/reducer/truckManagementSlices/maintenanceSlice';
import { getInspectionView } from '../../redux/reducer/truckManagementSlices/inspectionSlice';

const validationSchema = yup.object().shape({
  vehicle: yup.string().required('Vehicle Number is required').nullable(),
  startDate: yup
    .date()
    .required('Start Date is required')
    .nullable()
    .typeError('Start Date is invalid'),
  endDate: yup
    .date()
    .required('End Date is required')
    .nullable()
    .typeError('End Date is invalid')
});

interface ReportData {
  startDate: Date | null;
  endDate: Date | null;
  vehicle: string | null;
}

const Inspection = () => {
  const [isDateDisabled, setIsDateDisabled] = useState<boolean>(false);
  const [getStartDate, setGetStartDate] = useState<Date | null>(null);
  const [getEndDate, setGetEndDate] = useState<Date | null>(null);
  const [toDateDisabled, setToDateDisabled] = useState<boolean>(true);
  const [showDataGrid, setShowDataGrid] = useState<boolean>(false);
  const [fromTimestamp, setFromTimeStamp] = useState<number | null>(null);
  const [currentTimestamp, setCurrentTimeStamp] = useState<number | null>(null);
  const [toTimeStamp, setToTimeStamp] = useState<number | null>(null);
  const [inspectionRows, setInspectionRows] = useState([]);
  const dispatch = useAppDispatch();
  const createAbort = useAbort();
  const { data } = useAppSelector(state => state.truckNumbers);
  const { data: inspectionData, isLoading } = useAppSelector(
    state => state.getInspection
  );
  const currentDate = new Date();

  const { control, handleSubmit, setValue, clearErrors, reset } = useForm({
    resolver: yupResolver(validationSchema)
  });

  //function to get maintenance report details
  const handleGetReportClick: SubmitHandler<ReportData> = async data => {
    setIsDateDisabled(false);
    const payload = {
      deviceID: data.vehicle,
      startDate: convertToEpoch(data.startDate),
      endDate: convertToEpoch(data.endDate),
      pageno: 1,
      pagesize: 10
    };
    dispatch(getInspectionView({ ...payload, signal: createAbort().abortCall.signal }));
    reset();
    setGetStartDate(null);
    setGetEndDate(null);
    setToDateDisabled(true);
    setShowDataGrid(true);
  };

  //function to handle Custom date options in From date field
  const handleCustomFrom = (start: Date, end: Date) => {
    if (start) {
      setToDateDisabled(false);
    }
    setValue('startDate', start);
    setGetStartDate(start);
    setValue('endDate', end);
    clearErrors('startDate');
    clearErrors('endDate');
    setGetEndDate(end);
  };

  //function to handle From date
  const handleFromDate = (date: any) => {
    if (date === null) return;
    setGetStartDate(date?.$d);
    const fromDate: number | null = convertToEpoch2(date?.$d);
    setFromTimeStamp(fromDate);
    setCurrentTimeStamp(convertToEpoch(currentDate));
    setValue('startDate', date?.$d);
    clearErrors('startDate');
    clearErrors('endDate');
    setGetEndDate(new Date(date?.$d?.getTime() + 1000 * 600));
    setValue('endDate', new Date(date?.$d?.getTime() + 1000 * 600));
    if (date) {
      setToDateDisabled(false);
    }
    if (fromDate && currentTimestamp !== null && fromDate > currentTimestamp) {
      dispatch(
        updateToast({
          show: true,
          message: 'From date is Invalid',
          severity: 'error'
        })
      );
    }
  };

  //function to handle Custom date options in To date field
  const handleCustomTo = (start: Date, end: Date) => {
    setValue('startDate', start);
    setGetStartDate(start);
    setValue('endDate', end);
    setGetEndDate(end);
    clearErrors('startDate');
    clearErrors('endDate');
  };

  //function to handle To date
  const handleToDate = (date: any) => {
    if (getStartDate === null) {
      setGetEndDate(date.$d);
      setValue('endDate', date?.$d);
      clearErrors('endDate');
      dispatch(
        updateToast({
          show: true,
          message: 'Please select "From Date"',
          severity: 'error'
        })
      );
    } else {
      setGetEndDate(date.$d);
      setValue('endDate', date?.$d);
      clearErrors('endDate');
    }
    const toDate: number | null = convertToEpoch2(date?.$d);
    setToTimeStamp(toDate);
    if (toDate && fromTimestamp != null && toDate < fromTimestamp) {
      dispatch(
        updateToast({
          show: true,
          message: 'To Date is invalid',
          severity: 'error'
        })
      );
      setValue('endDate', date?.$d);
      clearErrors('endDate');
    }
  };

  const truckNumbersDrop =
    data?.length > 0
      ? data?.map((value: any, index: number) => ({
          id: value,
          label: value
        }))
      : [];

  let columns = [
    {
      field: 'id',
      headerName: `S.No`,
      flex: 1,
      minWidth: 100
    },
    {
      field: 'company',
      headerName: `Company Name`,
      flex: 1,
      minWidth: 150
    },
    {
      field: 'role',
      headerName: `Role`,
      flex: 1,
      minWidth: 150
    },
    {
      field: 'firstName',
      headerName: `First Name`,
      flex: 1,
      minWidth: 150
    },
    {
      field: 'lastName',
      headerName: `Last Name`,
      flex: 1,
      minWidth: 150
    },
    {
      field: 'mobile',
      headerName: `Mobile No.`,
      flex: 1,
      minWidth: 150
    },
    {
      field: 'email',
      headerName: `Email ID`,
      flex: 1,
      minWidth: 150
    },
    {
      field: 'address',
      headerName: `Address`,
      flex: 1,
      minWidth: 150
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      minWidth: 150,
      renderCell: ({ row }: any) => (
        <Box>
          <Typography
            className='status'
            sx={{
              backgroundColor: row.status === 'Completed' ? '#9fff9f' : '#ffddaa',

              color: row.status === 'Completed' ? 'green' : '#ff9900'
            }}
          >
            {row.status}
          </Typography>
        </Box>
      )
    }
  ];

  useEffect(() => {
    if (inspectionData?.length > 0) {
      setInspectionRows(inspectionData);
    }
  }, [inspectionData]);

  useEffect(() => {
    dispatch(truckNumbers(createAbort().abortCall.signal));
    return () => {
      createAbort().abortCall.abort();
    };
  }, []);

  return (
    <>
      <Box className='inspection'>
        <Grid
          container
          gap={1.2}
          component='form'
          position={'relative'}
          onSubmit={handleSubmit(handleGetReportClick)}
          columnSpacing={{ xs: 10, sm: 2, md: 3 }}
          alignItems={'center'}
          marginBottom={1.5}
        >
          <Grid
            item
            sm={5.5}
            xs={5.5}
            lg={2}
            className=' animate__animated animate__slideInRight'
          >
            <CustomSelect
              id='select-vehicle'
              options={truckNumbersDrop}
              control={control}
              name='vehicle'
              placeholder='Vehicle'
              className='reports-select'
            />
          </Grid>
          <Grid
            item
            sm={5.5}
            xs={5.5}
            lg={2}
            className=' animate__animated animate__slideInRight'
          >
            <CustomDateTimeCalendar
              id='select-startdate'
              name='startDate'
              control={control}
              disableFuture={true}
              disablePast={false}
              value={getStartDate}
              format='DD/MM/YYYY hh:mm a'
              placeholder='Start Date'
              getStartDate={getStartDate}
              isDisabled={isDateDisabled}
              className='reports-calendar'
              onDateSelect={(start: Date, end: Date) => handleCustomFrom(start, end)}
              onDateChange={(date: Date) => handleFromDate(date)}
              type='report'
            />
          </Grid>
          <Grid
            item
            sm={5.5}
            xs={5.5}
            lg={2}
            className=' animate__animated animate__slideInRight'
          >
            <CustomDateTimeCalendar
              id='select-enddate'
              name='endDate'
              control={control}
              placeholder='End Date'
              format='DD/MM/YYYY hh:mm a'
              disableFuture={true}
              disablePast={false}
              className='reports-calendar'
              getStartDate={getStartDate}
              value={getEndDate}
              onDateSelect={(start: Date, end: Date) => handleCustomTo(start, end)}
              onDateChange={(date: Date) => handleToDate(date)}
              minDateTime={
                getStartDate !== null ? dayjs(getStartDate?.getTime() + 1000 * 600) : null
              }
              maxDateTime={dayjs()
                .set('hour', new Date().getHours())
                .set('minutes', new Date().getMinutes())
                .startOf('minute')}
              isDisabled={toDateDisabled}
              type='report'
            />
          </Grid>
          <Grid
            item
            lg={3.5}
            md={3.5}
            sm={5.5}
            xs={5.5}
            className=' animate__animated animate__slideInRight'
          >
            <CustomButton className='get-report' category='Get Report' type='submit' />
          </Grid>
        </Grid>
        {showDataGrid && (
          <CustomDataGrid rows={inspectionData} columns={columns} loading={isLoading} />
        )}
      </Box>
    </>
  );
};

export default Inspection;
